import type { DependencyGroup } from "../../shared/dependencies";
import { formatCount, formatRelative } from "../api";
import { providerFromString, type ProviderId } from "../brands";
import { providerName } from "./loopPlacements";

/**
 * The spine of the loop detail page: the section list the sticky nav walks, the fold state of the
 * secondary sections, and the one-line summaries each fold shows while closed. Pure so the page
 * component only wires DOM events to it.
 */

export type SectionId = "runs" | "performance" | "notes" | "definition" | "dependencies" | "diagnostics" | "details";
export type FoldSection = Extract<SectionId, "notes" | "definition" | "dependencies" | "details">;

export const LOOP_DETAIL_SECTIONS: ReadonlyArray<{ id: SectionId; label: string }> = [
  { id: "runs", label: "Runs" },
  { id: "performance", label: "Performance" },
  { id: "notes", label: "Notes" },
  { id: "definition", label: "Definition" },
  { id: "dependencies", label: "Dependencies" },
  { id: "diagnostics", label: "Diagnostics" },
  { id: "details", label: "Details" },
];

export const FOLD_SECTIONS: readonly FoldSection[] = ["notes", "definition", "dependencies", "details"];

export type FoldStorage = Pick<Storage, "getItem" | "setItem">;

export function foldStorageKey(section: FoldSection): string {
  return `lg.loopDetail.open.${section}`;
}

/** The remembered state for a fold: true, false, or null when nothing usable is stored (or storage is blocked). */
export function readFoldState(storage: FoldStorage | null | undefined, section: FoldSection): boolean | null {
  if (!storage) return null;
  try {
    const value = storage.getItem(foldStorageKey(section));
    return value === "1" ? true : value === "0" ? false : null;
  } catch {
    return null;
  }
}

export function writeFoldState(storage: FoldStorage | null | undefined, section: FoldSection, open: boolean): void {
  if (!storage) return;
  try {
    storage.setItem(foldStorageKey(section), open ? "1" : "0");
  } catch {
    // Storage blocked or full; the fold still works for this visit.
  }
}

/** Notes, Definition, and Dependencies open when they have something to show; Details starts closed. */
export function foldDefault(section: FoldSection, hasContent: boolean): boolean {
  return section === "details" ? false : hasContent;
}

export function resolveFoldOpen(stored: boolean | null, section: FoldSection, hasContent: boolean): boolean {
  return stored ?? foldDefault(section, hasContent);
}

/** The nav's active link: the first section in reading order that is in view, else what was active before. */
export function activeSection(order: readonly SectionId[], visible: ReadonlySet<SectionId>, fallback: SectionId): SectionId {
  return order.find((id) => visible.has(id)) ?? fallback;
}

export function notesSummary(notes: ReadonlyArray<{ createdAt: string }>): string {
  if (!notes.length) return "No notes yet";
  const newest = notes.reduce((latest, note) => (note.createdAt > latest ? note.createdAt : latest), notes[0].createdAt);
  return `${formatCount(notes.length, "note")} · last ${formatRelative(newest)}`;
}

export function definitionSummary(latest: { version: number; author: { name: string }; createdAt: string } | null): string {
  if (!latest) return "No definition recorded";
  return `Definition v${latest.version} · ${latest.author.name} · ${formatRelative(latest.createdAt)}`;
}

/** "{n} dependencies · {r} required · {c} confirmed by {Agent}", the last part only when the loop's current agent is known. */
export function dependenciesSummary(groups: readonly DependencyGroup[], currentProvider: ProviderId | null | undefined): string {
  if (!groups.length) return "No dependencies recorded";
  const parts = [formatCount(groups.length, "dependency", "dependencies"), `${groups.filter((group) => group.required).length} required`];
  if (currentProvider) {
    const confirmed = groups.filter((group) => group.entries.some((item) => item.confidence === "confirmed" && providerFromString(item.provider) === currentProvider)).length;
    parts.push(`${confirmed === 0 ? "none" : confirmed} confirmed by ${providerName(currentProvider)}`);
  }
  return parts.join(" · ");
}

export function detailsSummary(input: { sourceName: string | null | undefined; confidence: string | null | undefined; ownerName: string | null | undefined }): string {
  const parts = input.sourceName ? [input.sourceName, input.confidence || "confidence unrated"] : ["Declared manually"];
  parts.push(input.ownerName ? `owner ${input.ownerName}` : "owner not assigned");
  return parts.join(" · ");
}

export type ReliabilityFact = { value: string; state: "value" | "withheld"; detail: string };

/** Reliability with its sample once three outcomes resolved; an em dash that names the gate before that. */
export function reliabilityFact(performance: { runs: number; successes: number; resolvedRuns: number; successRate: number | null }): ReliabilityFact {
  const evaluable = performance.resolvedRuns >= 3 && performance.successRate != null;
  if (!evaluable) return { value: "—", state: "withheld", detail: `withheld · ${performance.resolvedRuns} of 3 resolved` };
  return { value: `${Math.round(performance.successRate! * 100)}%`, state: "value", detail: `n = ${performance.resolvedRuns} · ${performance.successes} of ${performance.resolvedRuns} resolved succeeded` };
}

export function runsFact(performance: { runs: number; resolvedRuns: number }): { value: string; detail: string } {
  if (performance.runs === 0) return { value: "0", detail: "no runs reported yet" };
  const unresolved = Math.max(performance.runs - performance.resolvedRuns, 0);
  return { value: String(performance.runs), detail: `${performance.resolvedRuns} resolved · ${unresolved} unresolved` };
}
